import axios from "axios";
import CryptoJS from "crypto-js";
import accessToken from "./jwt-token-access/accessToken";
import config from "../config/config";

export const DECRYPT_KEY = config.DECRYPT_KEY || "58g9Q5UaDXc_AiJzg9SHstfOmiHiaNzo0CeYVaznnK4=";

function base64ToBytes(b64) {
  let norm = b64.replace(/-/g, "+").replace(/_/g, "/");
  while (norm.length % 4 !== 0) norm += "=";
  const bin = atob(norm);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

function uint8ArrayToWordArray(u8arr) {
  const words = [];
  for (let i = 0; i < u8arr.length; i += 4) {
    words.push(
      ((u8arr[i] || 0) << 24) |
      ((u8arr[i + 1] || 0) << 16) |
      ((u8arr[i + 2] || 0) << 8) |
      (u8arr[i + 3] || 0)
    );
  }
  return CryptoJS.lib.WordArray.create(words, u8arr.length);
}

/**
 * Decrypts a Fernet token string produced by Python cryptography.fernet
 */
export function decryptFernetToken(tokenStr, keyB64 = DECRYPT_KEY) {
  if (!tokenStr || typeof tokenStr !== "string") return tokenStr;

  let cleanedToken = tokenStr.trim();
  if ((cleanedToken.startsWith('"') && cleanedToken.endsWith('"')) ||
      (cleanedToken.startsWith("'") && cleanedToken.endsWith("'"))) {
    cleanedToken = cleanedToken.slice(1, -1).trim();
  }

  if (!cleanedToken.startsWith("gAAAAA")) {
    return tokenStr;
  }

  try {
    const keyBytes = base64ToBytes(keyB64);
    const encryptionKeyBytes = keyBytes.slice(16, 32);
    const tokenBytes = base64ToBytes(cleanedToken);

    if (tokenBytes.length < 57 || tokenBytes[0] !== 0x80) {
      return tokenStr;
    }

    const ivBytes = tokenBytes.slice(9, 25);
    const ciphertextBytes = tokenBytes.slice(25, tokenBytes.length - 32);

    const encKeyWordArray = uint8ArrayToWordArray(encryptionKeyBytes);
    const ivWordArray = uint8ArrayToWordArray(ivBytes);
    const ciphertextWordArray = uint8ArrayToWordArray(ciphertextBytes);

    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext: ciphertextWordArray },
      encKeyWordArray,
      { iv: ivWordArray, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }
    );

    const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);
    if (!decryptedText) return tokenStr;

    try {
      return JSON.parse(decryptedText);
    } catch (e) {
      return decryptedText;
    }
  } catch (err) {
    console.error("Fernet decryption failed:", err);
    return tokenStr;
  }
}

/**
 * Helper to process any API response data and decrypt encrypted fields/tokens
 */
export function decryptApiResponse(data) {
  if (!data) return data;

  if (typeof data === "string") {
    return decryptFernetToken(data);
  }

  if (typeof data === "object") {
    for (const key of Object.keys(data)) {
      if (typeof data[key] === "string" && data[key].trim().startsWith("gAAAAA")) {
        const decryptedContent = decryptFernetToken(data[key]);
        if (decryptedContent !== data[key]) {
          if (typeof decryptedContent === "object" && decryptedContent !== null && !Array.isArray(decryptedContent)) {
            return {
              ...data,
              ...decryptedContent,
              [key]: decryptedContent,
            };
          }
          return {
            ...data,
            [key]: decryptedContent,
          };
        }
      }
    }
  }

  return data;
}

//pass new generated access token here
const token = accessToken;

//apply base url for axios
const API_URL = "";

const axiosApi = axios.create({
  baseURL: API_URL,
});

axiosApi.defaults.headers.common["Authorization"] = token;

axiosApi.interceptors.request.use(
  config => {
    let accessToken = localStorage.getItem("access_token")
    if (!accessToken) {
      const tokenVal = localStorage.getItem("Token")
      if (tokenVal) {
        try {
          accessToken = JSON.parse(tokenVal)
        } catch (e) {
          accessToken = tokenVal
        }
      }
    }
    if (accessToken) {
      config.headers = config.headers || {}
      const hasAuth = Object.keys(config.headers).some(
        key => key.toLowerCase() === "authorization"
      )
      if (!hasAuth) {
        config.headers["Authorization"] = `Bearer ${accessToken}`
      }
    }
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

let isLoggingOut = false;

export async function globalLogout() {
  if (isLoggingOut) return;
  isLoggingOut = true;

  let user = null;
  try {
    const userVal = localStorage.getItem("User");
    if (userVal) {
      user = JSON.parse(userVal);
    }
  } catch (e) {
    console.error("Error parsing User from localStorage", e);
  }

  const isAdmin = user === "Admin";
  const logoutUrl = isAdmin
    ? `${config.API_URL}/Admin/Admin_Logout_Api/`
    : `${config.API_URL}/Teacher/TeacherLogout_Api/`;

  const targetPath = isAdmin ? "/login" : "/teacher";
  const currentPath = window.location.pathname;

  if (currentPath === targetPath || currentPath === targetPath + "/") {
    localStorage.clear();
    isLoggingOut = false;
    return;
  }

  try {
    const token = localStorage.getItem("access_token") || (localStorage.getItem("Token") ? JSON.parse(localStorage.getItem("Token")) : null);
    if (token) {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(logoutUrl, {}, { headers, skipInterceptor: true });
    }
  } catch (error) {
    console.error("Error calling logout API", error);
  }

  localStorage.clear();
  isLoggingOut = false;
  window.location.href = targetPath;
}

axiosApi.interceptors.response.use(
  (response) => {
    if (response.config && response.config.skipInterceptor) {
      return response;
    }
    if (response.data) {
      response.data = decryptApiResponse(response.data);
    }
    const token = localStorage.getItem("access_token") || localStorage.getItem("Token");
    if (token && response.data && response.data.status === 403 && response.data.msg === "Invalid token") {
      globalLogout();
    }
    return response;
  },
  (error) => {
    if (error.config && error.config.skipInterceptor) {
      return Promise.reject(error);
    }
    if (error.response && error.response.data) {
      error.response.data = decryptApiResponse(error.response.data);
    }
    const token = localStorage.getItem("access_token") || localStorage.getItem("Token");
    if (
      token &&
      error.response &&
      error.response.data &&
      (error.response.data.status === 403 || error.response.status === 403) &&
      error.response.data.msg === "Invalid token"
    ) {
      globalLogout();
    }
    return Promise.reject(error);
  }
);

export async function get(url, config = {}) {
  return await axiosApi
    .get(url, { ...config })
    .then((response) => decryptApiResponse(response.data));
}

export async function post(url, data, config = {}) {
  return axiosApi
    .post(url, { ...data }, { ...config })
    .then((response) => decryptApiResponse(response.data));
}

export async function put(url, data, config = {}) {
  return axiosApi
    .put(url, { ...data }, { ...config })
    .then((response) => decryptApiResponse(response.data));
}

export async function del(url, config = {}) {
  return await axiosApi
    .delete(url, { ...config })
    .then((response) => decryptApiResponse(response.data));
}

